import { useSearchParams } from 'react-router-dom';
import { useOrganisationFeaturesOf } from 'app/hooks/useOrganisationFeatures';
import { useOrganisationFeatureFlags } from './useOrganisationFeatureFlags';
import { useHasActiveWebsitePlan, useHasBespokePlan } from './useSubscription';

export interface UseWebBuilderAccessResult {
  hasWebBuilderAccess: boolean | undefined;
  /** Support switched the builder off for the organisation, so it is neither shown nor upsold. */
  isHidden: boolean;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

export function useViewedOrganisationId(): string {
  const [searchParams] = useSearchParams();
  return searchParams.get('organisationId') ?? '';
}

// Access is granted when ANY of these conditions are true:
// 1. Organisation has an active Chargebee plan matching Plus or Bespoke
// 2. The webBuilderAccessEnabled feature flag is toggled on (support override)
// 3. The hasBespoke feature flag is toggled on (bespoke without Chargebee plan)
export function useWebBuilderAccess(): UseWebBuilderAccessResult {
  const organisationId = useViewedOrganisationId();
  const features = useOrganisationFeaturesOf(organisationId);
  // Today's checks only decide anything before launch, which the plan features say.
  const todayChecks = {
    enabled: !features.isLoading && !features.isError && !features.pricingLaunched,
  };
  const flags = useOrganisationFeatureFlags(todayChecks);
  const plan = useHasActiveWebsitePlan(organisationId, todayChecks);

  const retry = () => {
    features.retry();
    if (todayChecks.enabled) {
      flags.refetch();
      plan.refetch();
    }
  };

  // The header's organisation selector puts the viewed organisation in the URL once it has loaded.
  if (!organisationId) {
    return {
      hasWebBuilderAccess: undefined,
      isHidden: false,
      isLoading: true,
      isError: false,
      retry,
    };
  }

  if (features.isLoading || features.isError) {
    return {
      hasWebBuilderAccess: undefined,
      isHidden: false,
      isLoading: features.isLoading,
      isError: features.isError,
      retry,
    };
  }

  if (features.pricingLaunched) {
    return {
      hasWebBuilderAccess: features.hasFeature('webBuilderAccessEnabled'),
      isHidden: features.isFeatureHidden('webBuilderAccessEnabled'),
      isLoading: false,
      isError: false,
      retry,
    };
  }

  return {
    hasWebBuilderAccess:
      plan.data || flags.featureFlags?.webBuilderAccessEnabled || flags.featureFlags?.hasBespoke,
    isHidden: false,
    isLoading: flags.isLoading || plan.isLoading,
    isError: flags.isError || plan.isError,
    retry,
  };
}

export interface UseIsBespokeResult {
  isBespoke: boolean | undefined;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

// Bespoke is true when the Chargebee plan is bespoke OR the support flag is set.
export function useIsBespoke(): UseIsBespokeResult {
  const organisationId = useViewedOrganisationId();
  const features = useOrganisationFeaturesOf(organisationId);
  const todayChecks = {
    enabled: !features.isLoading && !features.isError && !features.pricingLaunched,
  };
  const flags = useOrganisationFeatureFlags(todayChecks);
  const plan = useHasBespokePlan(organisationId, todayChecks);

  const retry = () => {
    features.retry();
    if (todayChecks.enabled) {
      flags.refetch();
      plan.refetch();
    }
  };

  if (!organisationId) return { isBespoke: undefined, isLoading: true, isError: false, retry };

  if (features.isLoading || features.isError) {
    return {
      isBespoke: undefined,
      isLoading: features.isLoading,
      isError: features.isError,
      retry,
    };
  }

  if (features.pricingLaunched) {
    return {
      isBespoke: features.hasFeature('hasBespoke'),
      isLoading: false,
      isError: false,
      retry,
    };
  }

  return {
    isBespoke: plan.data || flags.featureFlags?.hasBespoke,
    isLoading: flags.isLoading || plan.isLoading,
    isError: flags.isError || plan.isError,
    retry,
  };
}
